import classnames from 'classnames';
import { noop } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { CalculatorOptionType, CalculatorPadOptionDB, CalculatorPadOptionUI } from '../../admin/calculator/store';
import { getAllDropdownLists } from '../../admin/dropdown-lists/store';
import { Dropdown } from '../dropdown/Dropdown';
import { Icon } from '../icon/Icon';
import { Tick } from '../icons';
import { Number } from '../number/Number';
import styles from './Calculator.module.scss';
import { DatePicker } from '../datepicker/DatePicker';

interface ReadOnlyCalculatorPadOptionProps {
    calculatorPadOption: CalculatorPadOptionDB | CalculatorPadOptionUI;
    selected: boolean;
    select: (value: CalculatorPadOptionDB | CalculatorPadOptionUI) => void;
    inputWrapperWidth: string;
}

export const ReadOnlyCalculatorPadOption: React.FC<ReadOnlyCalculatorPadOptionProps> = ({
    calculatorPadOption,
    select,
    selected,
    inputWrapperWidth,
}) => {
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const { label, type, config: { inputFontSize, dropdownName }, calculatorPadOptionId } = calculatorPadOption;

    const getDropdownOptions = useCallback((dropdownName: string) => {
        if (dropdownName === 'LTST') {
            return [{ value: 'LT', label: 'LT' }, { value: 'ST', label: 'ST' }];
        }

        if (dropdownName === 'AffectedParty') {
            return [{ value: 'Party A', label: 'Party A' }, { value: 'Party B', label: 'Party B' }, { value: 'Both Parties', label: 'Both Parties' }];
        }

        const list = dropdownLists.find(({ name }) => name === dropdownName)?.options || [];
        return list.map(value => ({ value, label: value }));
    }, [dropdownLists]);

    const inputContent = useMemo(() => {
        switch (type) {
            case CalculatorOptionType.DROPDOWN: {
                const dropdownOptions = getDropdownOptions(dropdownName!);
                return <div className={styles.dropdownOption}>
                    <Dropdown
                        options={dropdownOptions}
                        value={null}
                        onChange={noop}
                        menuPortalTarget={document.body}
                        hideIndicator
                        maxControlHeight='30px'
                        minControlHeight='30px'
                    />
                </div>;
            }
            case CalculatorOptionType.NUMBER:
                return <div className={styles.numberOption}>
                    <Number
                        value={0}
                        onChange={noop}
                        height='18px'
                    />
                    <div className={styles.numberButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.DECIMAL:
                return <div className={styles.numberOption}>
                    <Number
                        value={0}
                        onChange={noop}
                        allowDecimal
                        height='18px'
                    />
                    <div className={styles.numberButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.DATE:
                return <div className={styles.dateOption}>
                    <DatePicker
                        value={null}
                        onChange={noop}
                        reducedHeight
                        testId={`read-only-calc-pad-option-date-${calculatorPadOptionId}`}
                        disabled
                        placeholder='Select...'
                    />
                    <div className={styles.dateButton}><Icon icon={Tick} fontSize={18} /></div>
                </div>;
            case CalculatorOptionType.TEXT:
            case CalculatorOptionType.STANDARD:
            default:
                return null;
        }
    }, [getDropdownOptions, type, dropdownName, calculatorPadOptionId]);

    return (
        <button className={classnames(styles.readOnlyInputWrapper, { [styles.selectedInputWrapper]: selected })} style={{ width: inputWrapperWidth }}>
            <div className={styles.inputLabel} style={{ fontSize: inputFontSize, cursor: 'pointer' }} onClick={() => select(calculatorPadOption)}>{label}</div>
            {inputContent}
        </button>
    );
};
